// @ts-strict-ignore
import { useSelector } from 'react-redux';
import type { ReactNode } from 'react';
import { Fragment, useMemo } from 'react';
import {
  selectActiveNodeEditorTab,
  selectFocusedAction,
  selectInitialWorkflowData,
  selectWorkflowData,
} from 'flow/store/redux/selectors';
import { useInitialWorkflowStore } from './useInitialWorkflowStore';
import {
  StyledDiffViewContainer,
  StyledDiffViewPane,
  StyledBadgeContainer,
  StyledDiffViewGrid,
  StyledDiffViewCardSlot,
} from './styles';
import { Badge } from '@usealloy/component-library';
import { panes } from './DiffContext';
import type { MergeDiffResult } from 'flow/service/changelog/formatters/mergeDropdownFormatter/types';
import { Operation } from 'flow/service/changelog/types';
import type { Action, SchemaInstance } from 'flow/store/engine/types';
import { DiffCard } from './DiffCard';
import { ActionType } from 'flow/store/engine/constants';

type DiffViewSplitProps = {
  children: ReactNode;
  action?: Action;
  showBadges?: boolean;
};

export function getDiffStatusById(diffContext: MergeDiffResult, id: string) {
  if (diffContext.added.has(id)) return Operation.Add;
  if (diffContext.modified.has(id)) return Operation.Modify;
  if (diffContext.removed.has(id)) return Operation.Remove;
  return null;
}

/**
 * Renders `children` twice, side by side: "Existing" against a store/form scoped to the
 * initial snapshot, and "Modified" against the ambient store/form. Each pane is wrapped in
 * its own DiffContextProvider so children can diff against each other.
 */
export function DiffViewSplit({ children, action, showBadges = true }: DiffViewSplitProps) {
  const initialWorkflowData = useSelector(selectInitialWorkflowData);
  const activeNodeEditorTab = useSelector(selectActiveNodeEditorTab);
  const selectedFocusedAction = useSelector(selectFocusedAction);
  const focusedAction = action ?? selectedFocusedAction;
  const initialStore = useInitialWorkflowStore();

  const initialFocusedAction = initialWorkflowData?.actions?.[focusedAction?.publicId];

  return (
    <StyledDiffViewContainer>
      <StyledDiffViewPane>
        {showBadges && (
          <StyledBadgeContainer>
            <Badge status="neutral">Existing</Badge>
          </StyledBadgeContainer>
        )}
        <DiffCard
          action={initialFocusedAction}
          pane={panes.left}
          store={initialStore}
          formTab={activeNodeEditorTab}
          child={children}
        />
      </StyledDiffViewPane>
      <StyledDiffViewPane>
        {showBadges && (
          <StyledBadgeContainer>
            <Badge status="attention">Modified</Badge>
          </StyledBadgeContainer>
        )}
        <DiffCard
          action={focusedAction}
          pane={panes.right}
          formTab={activeNodeEditorTab}
          child={children}
        />
      </StyledDiffViewPane>
    </StyledDiffViewContainer>
  );
}

type DiffRow = {
  id: string;
  current?: Action;
  initial?: Action;
};

type DiffViewActionsSplitProps = {
  children: (action: Action) => ReactNode;
  showBadges?: boolean;
};

function getOutcomeActionIds(workflowData: Partial<SchemaInstance>) {
  return Object.values(workflowData?.actions ?? {})
    .filter(action => action.type === ActionType.OUTCOME)
    .map(action => action.publicId);
}

/**
 * Diffs every outcome action side by side: one badge row per pane, then one aligned row
 * per outcome. Outcomes present on only one side (added or removed) render an empty slot
 * on the other, so paired rows stay level with each other.
 */
export function DiffViewOutcomeActionsSplit({
  children,
  showBadges = true,
}: DiffViewActionsSplitProps) {
  const initialWorkflowData = useSelector(selectInitialWorkflowData);
  const currentWorkflowData = useSelector(selectWorkflowData);
  const initialStore = useInitialWorkflowStore();

  const rows = useMemo<DiffRow[]>(() => {
    const allActionIds = new Set<string>([
      ...getOutcomeActionIds(initialWorkflowData),
      ...getOutcomeActionIds(currentWorkflowData),
    ]);

    return [...allActionIds]
      .map(id => ({
        id,
        initial: initialWorkflowData?.actions?.[id],
        current: currentWorkflowData?.actions?.[id],
      }));
  }, [initialWorkflowData, currentWorkflowData]);

  return (
    <StyledDiffViewGrid>
      {showBadges && (
        <>
          <StyledDiffViewCardSlot pane={panes.left}>
            <StyledBadgeContainer>
              <Badge status="neutral">Existing</Badge>
            </StyledBadgeContainer>
          </StyledDiffViewCardSlot>
          <StyledDiffViewCardSlot pane={panes.right}>
            <StyledBadgeContainer>
              <Badge status="attention">Modified</Badge>
            </StyledBadgeContainer>
          </StyledDiffViewCardSlot>
        </>
      )}
      {rows.map(row => (
        <Fragment key={row.id}>
          <DiffCard
            action={row.initial}
            child={row.initial ? children(row.initial) : null}
            pane={panes.left}
            store={initialStore}
          />
          <DiffCard
            action={row.current}
            child={row.current ? children(row.current) : null}
            pane={panes.right}
          />
        </Fragment>
      ))}
    </StyledDiffViewGrid>
  );
}
